<template>
  <View justify="center" align="center" height="full">
    <Card size="large">
      <View direction="column" gap="lg" align="center" padding="xl" class="fixed-height-container">
        <View class="status-icon-container" justify="center" align="center">
          <LoadingIndicator v-if="status === 'loading'" />
          <IconTile
            v-else-if="status === 'success'"
            icon="check-circle"
            size="large"
            color="green"
            shape="rounded"
            :colored-icon="true"
          />
          <IconTile
            v-else-if="status === 'error'"
            icon="x-close-circle"
            size="large"
            color="red"
            shape="rounded"
            :colored-icon="true"
          />
        </View>

        <View class="fixed-height-text" justify="center" align="center">
          <Text variant="h3" v-if="status === 'loading'">Processing Google Calendar authorization...</Text>
          <Text variant="h3" v-else-if="status === 'success'">Google Calendar connected successfully!</Text>
          <Text variant="h3" v-else-if="status === 'error'">Failed to connect Google Calendar</Text>
        </View>

        <View class="fixed-height-text" justify="center" align="center">
          <Text variant="body-large" color="secondary" v-if="status === 'loading'">
            Please wait while we complete the connection
          </Text>
          <Text variant="body-large" color="secondary" v-else-if="status === 'success'">
            This window will close automatically
          </Text>
          <Text variant="body-large" color="secondary" v-else-if="status === 'error'">
            Please try again. This window will close automatically
          </Text>
        </View>
      </View>
    </Card>
  </View>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';

import { googleCalendarService } from '@tallo/auth';
import { Card, IconTile, LoadingIndicator, Text, View } from '@tallo/design-system';
import { useInvestorStore } from '@tallo/investor/investor';

type Status = 'loading' | 'success' | 'error';

const status = ref<Status>('loading');
const investorStore = useInvestorStore();

function closeWindow() {
  window.close();
}

onMounted(async () => {
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const isPrimary = urlParams.get('isPrimary') !== 'false';

  if (code) {
    try {
      await googleCalendarService.handleAuthCallback(code, isPrimary);

      await investorStore.load();

      status.value = 'success';

      if (window.opener && !window.opener.closed) {
        window.opener.postMessage({ type: 'GOOGLE_CALENDAR_CONNECTED', success: true }, '*');
      }

      setTimeout(closeWindow, 1000);
    } catch (error) {
      console.error('Failed to complete Google Calendar authorization:', error);

      status.value = 'error';

      if (window.opener && !window.opener.closed) {
        window.opener.postMessage({ type: 'GOOGLE_CALENDAR_CONNECTED', success: false }, '*');
      }

      setTimeout(closeWindow, 1000);
    }
  } else {
    status.value = 'error';
    setTimeout(closeWindow, 1000);
  }
});
</script>

<style scoped lang="scss">
.fixed-height-container {
  width: 100%;
  min-width: 30rem;
}

.status-icon-container {
  height: 4rem;
}

.fixed-height-text {
  min-height: 3rem;
  text-align: center;
}
</style>
